import React, { useEffect, useState } from 'react';
import { X, Globe, ArrowRight } from 'lucide-react';
import { useRegion, REGIONS } from '../context/RegionContext';

const DISMISS_KEY = 'zhelta_geo_dismissed';

export default function GeoSuggestionBanner() {
  const { region, setRegion, detected } = useRegion();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch (_e) {
      return false;
    }
  });
  const [visible, setVisible] = useState(false);

  // Only surface the suggestion if:
  //  - geo-IP detected a country
  //  - the detected country differs from the user's current region
  //    (or no region selected yet)
  //  - user hasn't permanently dismissed
  useEffect(() => {
    if (!detected || dismissed) return;
    if (region === detected) return;
    const t = setTimeout(() => setVisible(true), 600); // gentle entrance
    return () => clearTimeout(t);
  }, [detected, region, dismissed]);

  const accept = () => {
    if (detected) setRegion(detected);
    setVisible(false);
  };

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch (_e) {
      /* ignore */
    }
    setDismissed(true);
    setVisible(false);
  };

  if (!visible || !detected) return null;
  const info = REGIONS.find((r) => r.code === detected);
  if (!info) return null;

  return (
    <div
      className="fixed left-4 right-4 sm:left-auto sm:right-6 bottom-24 sm:bottom-24 z-[90] sm:max-w-[360px] animate-in fade-in slide-in-from-bottom-4 duration-500"
      role="status"
      aria-live="polite"
      data-testid="geo-suggestion-banner"
    >
      <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_20px_50px_-10px_rgba(0,0,0,0.25)] overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white border border-gray-200">
            <Globe className="h-5 w-5 text-gray-700" strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700 leading-relaxed">
              Visiting from{' '}
              <span className="inline-flex items-baseline gap-1 font-semibold text-gray-900">
                <span className="text-base leading-none translate-y-0.5">{info.flag}</span>
                {info.name}
              </span>
              ? Switch to{' '}
              <span className="font-semibold uppercase tracking-wide text-gray-900">
                {info.lang} · {info.currency}
              </span>
              .
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                onClick={accept}
                data-testid="geo-suggestion-accept"
                className="inline-flex items-center gap-1 rounded-full bg-[#f5a623] text-[#0e0e10] text-[13px] font-semibold px-4 py-1.5 transition-colors hover:bg-[#e09611]"
              >
                Switch
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
              <button
                onClick={dismiss}
                data-testid="geo-suggestion-dismiss"
                className="inline-flex items-center rounded-full border border-gray-200 bg-white text-[13px] font-semibold text-gray-700 px-4 py-1.5 hover:bg-gray-50 transition-colors"
              >
                Keep current
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
          >
            <X className="h-4 w-4" strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </div>
  );
}
